import type { Storage } from '@tour-kit/core';
import {
  getGuidanceProgress,
  updateGuidanceProgress,
} from '@/core/guidance/guidance-progress-client';

/**
 * TourKit Storage that mirrors keys in localStorage and syncs the opaque
 * `storage` map to the Guidance Progress API (user scope).
 *
 * TourKit's persistence layer calls getItem/setItem **synchronously**
 * (`JSON.parse(storage.getItem("completed"))`). Async getItem returns a
 * Promise, which always parses as "not completed" — and the orientation
 * tour restarts on every reload. Hydrate from the server first, then expose
 * a sync localStorage-backed adapter.
 */
export async function createGuidanceTourStorage(
  userId: string,
): Promise<Storage> {
  const localPrefix = `guidance:${userId}:`;
  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  const localKey = (key: string) => `${localPrefix}${key}`;

  const readAllLocal = (): Record<string, string> => {
    const storage: Record<string, string> = {};
    try {
      for (let i = 0; i < localStorage.length; i += 1) {
        const fullKey = localStorage.key(i);
        if (!fullKey?.startsWith(localPrefix)) continue;
        const key = fullKey.slice(localPrefix.length);
        const value = localStorage.getItem(fullKey);
        if (value != null) storage[key] = value;
      }
    } catch {
      // ignore
    }
    return storage;
  };

  const scheduleFlush = () => {
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(() => {
      flushTimer = null;
      const storage = readAllLocal();
      void updateGuidanceProgress({
        workspaceId: null,
        storage,
      }).catch(() => {
        // Best-effort sync; local cache remains authoritative offline.
      });
    }, 400);
  };

  // Merge remote → local (do not wipe local keys missing remotely).
  try {
    const progress = await getGuidanceProgress(null);
    const remote = progress?.storage ?? {};
    for (const [key, value] of Object.entries(remote)) {
      try {
        localStorage.setItem(localKey(key), value);
      } catch {
        // ignore quota
      }
    }
  } catch {
    // Keep whatever is already in localStorage.
  }

  return {
    getItem: (key: string) => {
      try {
        return localStorage.getItem(localKey(key));
      } catch {
        return null;
      }
    },
    setItem: (key: string, value: string) => {
      try {
        localStorage.setItem(localKey(key), value);
      } catch {
        // ignore
      }
      scheduleFlush();
    },
    removeItem: (key: string) => {
      try {
        localStorage.removeItem(localKey(key));
      } catch {
        // ignore
      }
      scheduleFlush();
    },
  };
}
